<script>
import Spinner from "@/components/ui/Spinner";
import configService from "@/services/config";
import { mapGetters } from "vuex";
import Indications from "./Indications";

export default {
  name: "IndicationForm",

  props: {
    forms: {
      type: Array,
      required: true,
    },
    savedIndications: {
      type: Array,
      required: true,
    },
    appointment: {
      type: Object,
    },
    patient: {
      type: Object,
    },
  },

  components: {
    Spinner,
    Indications,
  },

  data() {
    return {
      isLoading: false,
      s3Url: configService.s3Url,
      isIndicationModalVisible: false,
      selectedIndication: null,
      isContentHidden: this.isCollapsable,
      filterModalShowed: false,
      indicationsFilter: [],
      appointmentStatus: this.status,
      typesSelected: [],
      /** @description Controla visibilidad del panel de signos vitales */
      showVitals: false,
      /** @description Valores de signos vitales a registrar */
      vitals: {
        systolic: null,
        diastolic: null,
        heartRate: null,
        temperature: null,
        weight: null,
        saturation: null,
        glucose: null,
      },
    };
  },

  computed: {
    ...mapGetters(["user", "isPatient"]),
  },

  created() {
    /**
     * @description Aplica texto de evolución y dispara búsqueda en DiagnosisSelect (CIE-10).
     *              Acepta string (legado) u objeto { description, diagnosisTerm }.
     */
    this.$bus.$on("ai-apply-evolution", (data) => {
      const text = typeof data === "string" ? data : data.description || "";
      const diagnosisTerm =
        typeof data === "object" ? data.diagnosisTerm : null;
      this.applyAISuggestion(
        "evolution",
        { description: text },
        diagnosisTerm
          ? { inputType: "diagnosisSelect", term: diagnosisTerm }
          : null
      );
    });
    /**
     * @description Por cada medicamento sugerido: agrega indicación con dosificación
     *              y pre-filtra MedicationSelect con el nombre genérico (ALFABETA).
     */
    this.$bus.$on("ai-apply-medications", (medications) => {
      medications.forEach((med) => {
        this.applyAISuggestion(
          "medications",
          { dosage: `${med.dose} — ${med.frequency} — ${med.duration}` },
          med.name ? { inputType: "medicationSelect", term: med.name } : null
        );
      });
    });
    /**
     * @description Por cada estudio de imagen: agrega indicación con observación
     *              y pre-filtra ImagesSelect con el nombre del estudio.
     */
    this.$bus.$on("ai-apply-imaging", (imaging) => {
      imaging.forEach((img) => {
        this.applyAISuggestion(
          "images",
          { observation: `${img.name}: ${img.reason}` },
          img.name ? { inputType: "imagesSelect", term: img.name } : null
        );
      });
    });
    /**
     * @description Por cada laboratorio sugerido: agrega indicación con observación
     *              y pre-filtra LabSelect con el nombre del estudio.
     */
    this.$bus.$on("ai-apply-lab", (lab) => {
      lab.forEach((item) => {
        this.applyAISuggestion(
          "lab",
          { observation: `${item.name}: ${item.reason}` },
          item.name ? { inputType: "labSelect", term: item.name } : null
        );
      });
    });
  },

  beforeDestroy() {
    this.$bus.$off("ai-apply-evolution");
    this.$bus.$off("ai-apply-medications");
    this.$bus.$off("ai-apply-imaging");
    this.$bus.$off("ai-apply-lab");
  },

  methods: {
    triggerUpload(createdIndication) {
      const refName = `indications-${createdIndication.type}`;
      this.$refs[refName][0].triggerUpload(createdIndication);
    },

    /**
     * @description Abre y expande una sección específica del formulario.
     * @param {string} type - Tipo de indicación a abrir
     */
    openSection(type) {
      const ref = this.$refs[`indications-${type}`];
      if (ref) {
        const component = Array.isArray(ref) ? ref[0] : ref;
        if (component) component.isSelected = true;
      }
    },

    /**
     * @description Guarda los signos vitales como VitalTasks del paciente.
     */
    async saveVitals() {
      try {
        const patientId =
          this.appointment?.patient?._id || this.appointment?.patient;
        if (!patientId) return;
        const organization = this.appointment?.organization;
        const payload = {
          entity: "patient",
          patient: patientId,
          organization,
          vitals: this.vitals,
          appointmentId: this.appointment?._id,
        };
        await this.$axios.post(`/tasks/vitals/from-appointment`, payload);
        this.$message({ type: "success", message: "Signos vitales guardados" });
        this.showVitals = false;
        this.vitals = {
          systolic: null,
          diastolic: null,
          heartRate: null,
          temperature: null,
          weight: null,
          saturation: null,
          glucose: null,
        };
      } catch (err) {
        this.$message({
          type: "error",
          message: "Error al guardar los signos vitales",
        });
      }
    },

    /**
     * @description Abre la sección del tipo indicado, rellena o reutiliza una indicación vacía
     *              y opcionalmente dispara una búsqueda en el selector dropdown correspondiente.
     * @param {string} type - Tipo de indicación ('evolution', 'medications', 'images', 'lab')
     * @param {Object} values - Valores de texto a precargar en campos del formulario
     * @param {Object|null} selectorSearch - { inputType: string, term: string } para pre-filtrar un selector
     */
    applyAISuggestion(type, values, selectorSearch) {
      const ref = this.$refs[`indications-${type}`];
      const component = Array.isArray(ref) ? ref[0] : ref;
      if (!component) return;
      // Abrir la sección para que sea visible y los refs estén activos
      component.isSelected = true;
      // Reusar indicación vacía existente en vez de siempre agregar una nueva
      const newIndication = component.getOrAddIndication();
      if (!newIndication) return;
      const newKey = newIndication.key;
      Object.keys(values).forEach((key) => {
        if (
          newIndication.defaultValues &&
          newIndication.defaultValues[key] !== undefined
        ) {
          this.$set(newIndication.defaultValues[key], "value", values[key]);
        }
      });
      component.updateIndication(newIndication);
      // Disparar búsqueda en selector tras actualización del DOM
      if (selectorSearch && selectorSearch.term) {
        this.$nextTick(() => {
          component.triggerSelectorSearch(
            newKey,
            selectorSearch.inputType,
            selectorSearch.term
          );
        });
      }
    },
  },
};
</script>

<template lang="pug">
.content
  template(v-for="(form, index) in forms")
    indications(
      :ref="`indications-${form.type}`"
      :key="form.type"
      :savedIndications="savedIndications"
      :form="form"
      :appointment="appointment"
      :patient="patient"
    )
    //- Quick actions debajo de evolución
    .quick-actions(v-if="form.type === 'evolution'")
      button.quick-action.quick-action--teal(@click="openSection('medications')")
        i.el-icon-plus
        span Medicamento
      button.quick-action.quick-action--navy(@click="openSection('lab')")
        i.el-icon-plus
        span Laboratorio
      button.quick-action.quick-action--navy(@click="openSection('images')")
        i.el-icon-plus
        span Imagen
      button.quick-action(@click="showVitals = !showVitals")
        i.el-icon-plus
        span Signos vitales
      button.quick-action(@click="openSection('certificates')")
        i.el-icon-plus
        span Certificado
      button.quick-action(@click="openSection('notes')")
        i.el-icon-plus
        span Nota
      button.quick-action(@click="openSection('practices')")
        i.el-icon-plus
        span Práctica
    //- Signos vitales después de imágenes (mismo estilo que el resto de secciones)
    .vitals-section(v-if="form.type === 'images'")
      .vitals-section__header(@click="showVitals = !showVitals")
        .vitals-section__left
          .vitals-section__icon(:class="{ 'vitals-section__icon--active': showVitals }")
            i.el-icon-monitor
          h3.vitals-section__title Signos vitales
        .vitals-section__chevron(:class="{ 'vitals-section__chevron--open': showVitals }")
          i.el-icon-arrow-down
      .vitals-section__body(v-show="showVitals")
        .vitals-grid
          .vital-item
            label.vital-label Presión arterial
            .vital-inputs
              input.vital-input(v-model="vitals.systolic" placeholder="120" type="number")
              span.vital-sep /
              input.vital-input(v-model="vitals.diastolic" placeholder="80" type="number")
            span.vital-unit mmHg
          .vital-item
            label.vital-label Frec. cardíaca
            input.vital-input(v-model="vitals.heartRate" placeholder="72" type="number")
            span.vital-unit lpm
          .vital-item
            label.vital-label Temperatura
            input.vital-input(v-model="vitals.temperature" placeholder="36.5" type="number")
            span.vital-unit °C
          .vital-item
            label.vital-label Peso
            input.vital-input(v-model="vitals.weight" placeholder="75" type="number")
            span.vital-unit kg
          .vital-item
            label.vital-label Sat. O₂
            input.vital-input(v-model="vitals.saturation" placeholder="98" type="number")
            span.vital-unit %
          .vital-item
            label.vital-label Glucemia
            input.vital-input(v-model="vitals.glucose" placeholder="90" type="number")
            span.vital-unit mg/dL
        .vitals-actions
          button.btn-vitals-save(@click="saveVitals") Guardar signos vitales
          button.btn-vitals-cancel(@click="showVitals = false") Cancelar
</template>

<style lang="scss">
.form-box {
  .el-timeline-item {
    &:last-child {
      padding-bottom: 0;
    }

    &__timestamp {
      margin: 0;
    }
  }
}
</style>
<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #eef2f7;
  min-height: 100%;
}

.quick-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 10px 0 4px;
}

.quick-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 5px;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  transition: all 0.1s;

  i {
    font-size: 11px;
  }

  &:hover {
    border-color: #94a3b8;
  }

  &--teal {
    background: #e0f7f7;
    color: #085041;
    border-color: #9fe1cb;
    &:hover {
      background: #c0eeea;
    }
  }

  &--navy {
    background: #e1eaf6;
    color: #0a3d7a;
    border-color: #b5d4f4;
    &:hover {
      background: #c8d8f0;
    }
  }
}

/* Signos vitales — mismo look que accordion-section de Indications.vue */
.vitals-section {
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.15s;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 13px 16px;
    cursor: pointer;
    background: #fff;
    border-bottom: 0.5px solid #f1f5f9;
    transition: background 0.15s;
    user-select: none;

    &:hover {
      background: #f8fafc;
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #eef2f7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s;

    i {
      font-size: 15px;
      color: #64748b;
      transition: color 0.15s;
    }

    &--active {
      background: #0d9d9d;

      i {
        color: #fff;
      }
    }
  }

  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #002d5e;
    margin: 0;
  }

  &__chevron {
    color: #94a3b8;
    font-size: 13px;
    transition: transform 0.2s;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding: 14px 16px;
  }
}

.vitals-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 14px;
}

.vital-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.vital-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.vital-inputs {
  display: flex;
  align-items: center;
  gap: 3px;
}

.vital-input {
  width: 62px;
  padding: 6px 8px;
  border: 0.5px solid #e2e8f0;
  border-radius: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  color: #334155;
  outline: none;

  &:focus {
    border-color: #0d9d9d;
  }
}

.vital-sep {
  font-size: 14px;
  color: #94a3b8;
}

.vital-unit {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
}

.vitals-actions {
  display: flex;
  gap: 8px;
}

.btn-vitals-save {
  background: #002d5e;
  color: white;
  padding: 7px 18px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: "Plus Jakarta Sans", sans-serif;

  &:hover {
    background: #0a3d7a;
  }
}

.btn-vitals-cancel {
  background: white;
  color: #475569;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 0.5px solid #e2e8f0;
  font-family: "Plus Jakarta Sans", sans-serif;
}
</style>
